"use strict";
/*
parse rect, polyline, polygon, polyline with or without transforms to
untransformed line segments, maintaining current styling

arcs are turned to line segments at given anchors

known issues - cannot handle global styling through svg classes, clipping masks
*/
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const svg_arc_to_cubic_bezier_1 = __importDefault(require("svg-arc-to-cubic-bezier"));
const svg_json_parser_1 = require("svg-json-parser");
const svg_path_parser_1 = require("svg-path-parser");
const transformation_matrix_1 = require("transformation-matrix");
const common_1 = require("../common");
const CreaseBuilder_1 = require("../core/CreaseBuilder");
const CreaseBuilderSegment_1 = require("../core/CreaseBuilderSegment");
// For computing cubic bezier control points elliptical arcs, from http://spencermortensen.com/articles/bezier-circle/
const KAPPA = 0.551915024494;
/**
 * A utility class for creating a Crease object from SVG.
 */
class SVGToCrease {
    /**
     * A matching rule for edge assignments.
     * @typedef {Object} Rule
     * @property {'color'|'dash'|'group'} type The type of rule.
     * @property {Object} value The value to match against. For a `color` rule, the value is
     * an RGB color specified as an array of three integers between 0 and 255 (matching edges must
     * have an RGB color within `tolerance` of this color). For a `dash` rule, the value can be
     * `true` (matching edges must have a `stroke-dasharray`), `false` (matching edges must not have
     * a `stroke-dasharray`), or an array of numbers (matching edges must have a `stroke-dasharray`
     * containing the same numbers within `tolerance`). For a `group` rule, the value is the string
     * ID (or an array of string IDs) of a containing group element.
     * @property {number} tolerance The tolerance used for a `color` or `dash` rule.
     */
    /**
     * Creates a Crease object from an SVG file.
     * @param {string} filePath The file path of the SVG file.
     * @param {Object} options Options for conversion to a Crease object.
     * @param {number} [options.distanceTolerance = 1] Distance tolerance for points to be merged.
     * @param {number} [options.angleTolerance = 0.001] Angle tolerance (in radians) for lines to
     * be considered collinear.
     * @param {Object} options.edgeAssignments Rules for making edge assignments.
     * @param {Rule[]} [options.edgeAssignments.mountains] Rules for mountain creases.
     * @param {Rule[]} [options.edgeAssignments.valleys] Rules for valley creases.
     * @param {Rule[]} [options.edgeAssignments.creases] Rules for unspecified creases.
     * @param {Rule[]} [options.edgeAssignments.cuts] Rules for cuts.
     * @param {Rule[]} [options.edgeAssignments.ignore] Rules for edges to ignore.
     * @returns {SVGToCreaseOutput} A Crease object and an array of warning
     * messages.
     */
    static loadSVG(fs, filePath, options) {
        if (typeof fs === 'string') {
            throw new Error('API change: loadSVG now takes fs as its first parameter.');
        }
        const svgString = fs.readFileSync(filePath, 'utf8');
        return SVGToCrease.parseSVG(svgString, options);
    }
    /**
     * Creates a Crease object from the contents of an SVG file.
     * @param {string} svgString The contents of an SVG file.
     * @param {Object} options Options for conversion to a Crease object.
     * @param {number} [options.distanceTolerance = 1] Distance tolerance for points to be merged.
     * @param {number} [options.angleTolerance = 0.001] Angle tolerance (in radians) for lines to
     * be considered collinear.
     * @param {Object} options.edgeAssignments Rules for making edge assignments.
     * @param {Rule[]} [options.edgeAssignments.mountains] Rules for mountain creases.
     * @param {Rule[]} [options.edgeAssignments.valleys] Rules for valley creases.
     * @param {Rule[]} [options.edgeAssignments.creases] Rules for unspecified creases.
     * @param {Rule[]} [options.edgeAssignments.cuts] Rules for cuts.
     * @param {Rule[]} [options.edgeAssignments.ignore] Rules for edges to ignore.
     * @returns {SVGToCreaseOutput} An array of Crease objects and an array of warning messages.
     */
    static parseSVG(svgString, options) {
        // Parse the SVG text.
        const svgJSON = svg_json_parser_1.parse(svgString);
        // Get segments from the parsed SVG.
        const ignoredTags = new Set();
        const allSegments = SVGToCrease._getSegmentsFromSVGNode(svgJSON, ignoredTags);
        const ignoredTagArray = Array.from(ignoredTags);
        const warnings = [];
        if (ignoredTagArray.length) {
            warnings.push(`Ignored these SVG tags: ${ignoredTagArray.join(', ')}.`);
        }
        // Filter out filled shapes.
        const unfilledSegments = allSegments.filter(segment => !segment.style.fill || segment.style.fill === 'none');
        // Use rules to determine edge assignments from styles.
        const unmatchedStyles = new Set();
        const creaseBuilderSegments = unfilledSegments.map(segment => {
            // Make sure fold angle is valid for the chosen edge assignment.
            let assignment = SVGToCrease._getEdgeAssignmentForStyle(segment.style, options, unmatchedStyles);
            let { foldAngle } = segment;
            if (foldAngle !== null) {
                if ((assignment === 'U' || assignment === 'M') && foldAngle < 0) {
                    // A negative fold angle means valley fold.
                    assignment = 'V';
                    foldAngle = -foldAngle;
                }
                else if (assignment === 'U' && foldAngle > 0) {
                    // A positive fold angle means mountain fold.
                    assignment = 'M';
                }
                else if (assignment === 'B' || assignment === 'C') {
                    // Boundary and cut edges cannot have fold angles.
                    foldAngle = null;
                }
            }
            return new CreaseBuilderSegment_1.CreaseBuilderSegment(segment.bezier, assignment, foldAngle);
        }).filter(segment => segment.assignment !== null);
        const unmatchedStylesArray = Array.from(unmatchedStyles);
        if (unmatchedStylesArray.length) {
            warnings.push(`No edge assignment rules matched these styles:\n  ${unmatchedStylesArray.join('\n  ')}`);
        }
        // Get tolerances.
        const distanceTolerance = options.distanceTolerance === undefined ? 1
            : options.distanceTolerance;
        const angleTolerance = options.angleTolerance === undefined ? 0.001
            : options.angleTolerance;
        // Dump segments for debugging.
        if (options.debugFs) {
            SVGToCrease._saveSegments(options.debugFs, creaseBuilderSegments);
        }
        // Construct a Crease object.
        try {
            const creaseBuilder = new CreaseBuilder_1.CreaseBuilder(creaseBuilderSegments);
            if (options.debugFs) {
                creaseBuilder.fs = options.debugFs;
                creaseBuilder.debug = true;
            }
            const creases = creaseBuilder.build(distanceTolerance, angleTolerance, options.progressCancelCallback);
            return { creases, warnings };
        }
        catch (error) {
            throw new Error(`${error.message}\nWarnings:\n${warnings.join('\n')}`);
        }
    }
    static _parseAttribute(attribute) {
        // Set default value to 0 if attribute not present.
        // (We see this assumption in svgs from XD).
        return parseFloat(attribute) || 0;
    }
    static _getSegmentsFromSVGNode(node, ignoredTags, parentStyle = {}) {
        let segments = [];
        // Ignore defs (used to define clipping masks), style, and text nodes for now.
        if (node.tag === 'defs' || node.tag === 'style' || node.tag === 'text') {
            ignoredTags.add(node.tag);
            return segments;
        }
        // Figure out styles applied to this node.
        const style = SVGToCrease._getStyleJSON(node);
        // If children don't have their own style, pass parent style down.
        Object.keys(parentStyle).forEach((key) => {
            if (style[key] === undefined) {
                style[key] = parentStyle[key];
            }
        });
        // Process parent nodes and leaf nodes separately.
        if (node.children) {
            // Recurse into children of the root node and group nodes.
            if (node.tag === 'svg' || node.tag === 'g') {
                node.children.forEach((child) => {
                    const childSegments = SVGToCrease._getSegmentsFromSVGNode(child, ignoredTags, style);
                    segments = segments.concat(childSegments);
                });
                // Use the group node's ID as an additional "style" attribute.
                if (node.id) {
                    segments.forEach((segment) => {
                        if (!segment.style.group) {
                            segment.style.group = node.id;
                        }
                    });
                }
            }
            else {
                throw new Error(`Unexpected children in SVG tag: ${node.tag}.`);
            }
        }
        else {
            // Process leaf nodes.
            let beziers;
            switch (node.tag) {
                case 'circle':
                    beziers = SVGToCrease._getBeziersFromCircle(node);
                    break;
                case 'line':
                    beziers = SVGToCrease._getBeziersFromLine(node);
                    break;
                case 'path':
                    beziers = SVGToCrease._getBeziersFromPath(node);
                    break;
                case 'polygon':
                    beziers = SVGToCrease._getBeziersFromPolygon(node);
                    break;
                case 'polyline':
                    beziers = SVGToCrease._getBeziersFromPolyline(node);
                    break;
                case 'rect':
                    beziers = SVGToCrease._getBeziersFromRect(node);
                    break;
                default:
                    // Ignore unrecognized tags.
                    ignoredTags.add(node.tag);
                    return segments;
            }
            // Get fold angle from the data-name attribute. This is where Illustrator's SVG export
            // puts the name of an object if it's not a valid ID. Numbers start with a digit or a
            // minus sign, making them invalid IDs.
            let foldAngle = parseFloat(node.attr['data-name']);
            if (Number.isNaN(foldAngle)) {
                foldAngle = null;
            }
            // Combine beziers, style, and fold angle into segments.
            segments = beziers.map(originalBezier => {
                // Elevate the degree of quadratic beziers to cubic, since we don't have
                // intersection code for quadratics at the moment.
                const bezier = SVGToCrease._elevateQuadraticToCubic(originalBezier);
                return { bezier, style, foldAngle };
            });
        }
        // Apply this node's transform.
        if (node.attr && node.attr.transform && node.attr.transform !== '') {
            // @ts-ignore
            const matrixDescriptors = transformation_matrix_1.fromTransformAttribute(node.attr.transform);
            // @ts-ignore
            const matrix = transformation_matrix_1.transform(transformation_matrix_1.fromDefinition(matrixDescriptors));
            segments.forEach((segment) => {
                // @ts-ignore
                segment.bezier = transformation_matrix_1.applyToPoints(matrix, segment.bezier);
            });
        }
        return segments;
    }
    static _elevateQuadraticToCubic(bezier) {
        if (bezier.length === 3) {
            const [p0, p1, p2] = bezier;
            return [
                p0,
                common_1.vec.add(common_1.vec.multiply(1 / 3, p0), common_1.vec.multiply(2 / 3, p1)),
                common_1.vec.add(common_1.vec.multiply(2 / 3, p1), common_1.vec.multiply(1 / 3, p2)),
                p2,
            ];
        }
        return bezier;
    }
    static _getStyleJSON(node) {
        const styleJSON = {};
        if (node.attr && node.attr.style && node.attr.style !== '') {
            const { style } = node.attr;
            const styleElements = style.split(';');
            styleElements.forEach((styleString) => {
                const trimmedStyleString = styleString.trim();
                if (trimmedStyleString === '') {
                    return;
                }
                const keyValue = trimmedStyleString.split(':');
                if (keyValue.length !== 2) {
                    throw new Error(`Invalid json: ${JSON.stringify(trimmedStyleString)}.`);
                }
                styleJSON[keyValue[0].trim().toLowerCase()] = keyValue[1].trim();
            });
        }
        // Parse stroke, fill, and dasharray to common form.
        ['stroke', 'fill', 'stroke-dasharray'].forEach((key) => {
            if (node.attr && node.attr[key]) {
                styleJSON[key] = node.attr[key];
            }
        });
        styleJSON.stroke = SVGToCrease._parseRGBColorString(styleJSON.stroke);
        styleJSON['stroke-dasharray'] = SVGToCrease._parseDashArrayString(styleJSON['stroke-dasharray']);
        return styleJSON;
    }
    static _parseRGBColorString(colorString) {
        if (!colorString || colorString === 'none') {
            return undefined;
        }
        // Use a regular expression to match colors like 'rgb(1.23%, 45.6%, 78.9%)'.
        const rgbPercentRegExp = /rgb\(\s*([0-9.]+)%\s*,\s*([0-9.]+)%\s*,\s*([0-9.]+)%\s*\)/;
        const rgbPercentMatch = colorString.match(rgbPercentRegExp);
        if (rgbPercentMatch) {
            return [1, 2, 3].map(i => Math.round(parseFloat(rgbPercentMatch[i]) * 255 / 100));
        }
        // Use a regular expression to match colors like 'rgb(255, 0, 0)'.
        const rgbRegExp = /rgb\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*\)/;
        const rgbMatch = colorString.match(rgbRegExp);
        if (rgbMatch) {
            return [1, 2, 3].map(i => parseInt(rgbMatch[i], 10));
        }
        // Parse colors like "03F", "#03f", "0033FF", and "#0033ff".
        // See https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb.
        const hexRegExp = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        const hex = colorString.replace(hexRegExp, (m, r, g, b) => r + r + g + g + b + b);
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (result) {
            return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
        }
        throw new Error(`Unknown color format: ${colorString}`);
    }
    static _parseDashArrayString(dashArrayString) {
        if (!dashArrayString) {
            return undefined;
        }
        // Use a regular expression to match dash arrays like '20, 14.5' or '13 24 34 13',
        // with commas and or spaces between numbers.
        const dashArrayRegExp = /\s*([0-9.]+)\s*,?/ug;
        const dashArrayMatches = [];
        while (true) { /* eslint-disable-line no-constant-condition */
            const match = dashArrayRegExp.exec(dashArrayString);
            if (match === null) {
                break;
            }
            // Add capture of group 1 to `matches`
            dashArrayMatches.push(parseFloat(match[1]));
        }
        return dashArrayMatches;
    }
    static _getBeziersFromCircle(circle) {
        const center = [
            SVGToCrease._parseAttribute(circle.attr.cx),
            SVGToCrease._parseAttribute(circle.attr.cy),
        ];
        const radius = SVGToCrease._parseAttribute(circle.attr.r);
        const quarterCircle = (xSign, ySign) => {
            const p1 = [center[0] + xSign * radius, center[1]];
            const p2 = [center[0] + xSign * radius, center[1] + ySign * KAPPA * radius];
            const p3 = [center[0] + xSign * KAPPA * radius, center[1] + ySign * radius];
            const p4 = [center[0], center[1] + ySign * radius];
            return [p1, p2, p3, p4];
        };
        return [
            quarterCircle(+1, +1),
            quarterCircle(+1, -1),
            quarterCircle(-1, -1),
            quarterCircle(-1, +1),
        ];
    }
    static _getBeziersFromLine(line) {
        const p1 = [
            SVGToCrease._parseAttribute(line.attr.x1),
            SVGToCrease._parseAttribute(line.attr.y1),
        ];
        const p2 = [
            SVGToCrease._parseAttribute(line.attr.x2),
            SVGToCrease._parseAttribute(line.attr.y2),
        ];
        return [[p1, p2]];
    }
    static _getBeziersFromPath(path) {
        // Parse the path's "d" attribute into segments, converting relative
        // coordinates to absolute coordinates.
        const pathCommands = svg_path_parser_1.makeAbsolute(svg_path_parser_1.parseSVG(path.attr.d));
        const beziers = [];
        let startPoint = [0, 0];
        let previousPoint = startPoint;
        let previousControlPoint = startPoint;
        let previousCode = '-';
        pathCommands.forEach((pathCommand) => {
            const command = pathCommand;
            const currentPoint = [command.x, command.y];
            switch (command.code) {
                case 'M': // move
                    startPoint = currentPoint;
                    previousPoint = currentPoint;
                    previousControlPoint = currentPoint;
                    break;
                case 'L': // line
                case 'H': // horizontal line
                case 'V': // vertical line
                    beziers.push([previousPoint, currentPoint]);
                    previousPoint = currentPoint;
                    previousControlPoint = currentPoint;
                    break;
                case 'Q': // quadratic bezier
                case 'T': // smooth quadratic bezier
                case 'C': // cubic bezier
                case 'S': { // smooth cubic bezier
                    const points = [previousPoint];
                    if (command.code === 'Q' || command.code === 'C') {
                        points.push([command.x1, command.y1]);
                    }
                    // Note that T only reflects the previous control point if the previous command
                    // was Q or T. Likewise, S only reflects if the previous command was C or S.
                    // Fixes bug #27.
                    const codesRequiredForReflection = {
                        T: 'QT',
                        S: 'CS',
                    };
                    if ((command.code === 'T' || command.code === 'S')
                        && codesRequiredForReflection[command.code].includes(previousCode)) {
                        // Reflect the previous curved segment's last control point across its
                        // last vertex to get the first control point of this segment.
                        const p = common_1.vec.add(previousPoint, common_1.vec.subtract(previousPoint, previousControlPoint));
                        points.push(p);
                    }
                    if (command.code === 'C' || command.code === 'S') {
                        points.push([command.x2, command.y2]);
                    }
                    points.push(currentPoint);
                    beziers.push(points);
                    previousPoint = currentPoint;
                    previousControlPoint = points[points.length - 2];
                    break;
                }
                case 'Z': // close path
                    beziers.push([previousPoint, startPoint]);
                    previousPoint = startPoint;
                    previousControlPoint = startPoint;
                    break;
                case 'A': { // elliptical arc
                    const curves = svg_arc_to_cubic_bezier_1.default({
                        px: previousPoint[0],
                        py: previousPoint[1],
                        cx: command.x,
                        cy: command.y,
                        rx: command.rx,
                        ry: command.ry,
                        xAxisRotation: command.xAxisRotation,
                        largeArcFlag: command.largeArc ? 1 : 0,
                        sweepFlag: command.sweep ? 1 : 0,
                    });
                    curves.forEach((curve) => {
                        const nextPoint = [curve.x, curve.y];
                        beziers.push([
                            previousPoint,
                            [curve.x1, curve.y1],
                            [curve.x2, curve.y2],
                            nextPoint,
                        ]);
                        previousPoint = nextPoint;
                    });
                    break;
                }
                default:
                    throw new Error(`Unknown command "${command.command}" with code "${command.code}".`);
            }
            previousCode = command.code;
        });
        return beziers;
    }
    static _getBeziersFromPolygon(polygon) {
        // Start with the line segments of a polyline.
        const lines = SVGToCrease._getBeziersFromPolyline(polygon);
        // Add a closing line segment.
        if (lines.length) {
            lines.push([lines[lines.length - 1][1], lines[0][0]]);
        }
        return lines;
    }
    static _getBeziersFromPolyline(polyline) {
        const pointsString = polyline.points || polyline.attr.points;
        const lines = [];
        if (pointsString === undefined) {
            return lines;
        }
        // match a pair of numbers separated by a comma or whitespace
        const regex = /\s*([0-9.]+)\s*(?:,|\s)\s*([0-9.]+)/ug;
        let match;
        let previousPoint;
        while (match = regex.exec(pointsString)) { /* eslint-disable-line no-cond-assign */
            const currentPoint = [parseFloat(match[1]), parseFloat(match[2])];
            if (previousPoint) {
                lines.push([previousPoint, currentPoint]);
            }
            previousPoint = currentPoint;
        }
        return lines;
    }
    static _getBeziersFromRect(rect) {
        const x = SVGToCrease._parseAttribute(rect.attr.x);
        const y = SVGToCrease._parseAttribute(rect.attr.y);
        const width = SVGToCrease._parseAttribute(rect.attr.width);
        const height = SVGToCrease._parseAttribute(rect.attr.height);
        if (!rect.attr.rx && !rect.attr.ry) {
            const p1 = [x, y];
            const p2 = [x + width, y];
            const p3 = [x + width, y + height];
            const p4 = [x, y + height];
            return [[p1, p2], [p2, p3], [p3, p4], [p4, p1]];
        }
        let rx = rect.attr.rx !== undefined ? SVGToCrease._parseAttribute(rect.attr.rx)
            : SVGToCrease._parseAttribute(rect.attr.ry);
        let ry = rect.attr.ry !== undefined ? SVGToCrease._parseAttribute(rect.attr.ry)
            : SVGToCrease._parseAttribute(rect.attr.rx);
        // Clamp to width/height.
        // This is identical to how illustrator treats these elements.
        if (rx > width / 2) {
            rx = width / 2;
        }
        if (ry > height / 2) {
            ry = height / 2;
        }
        const c = 1 - KAPPA;
        const p1 = [x + rx, y];
        const p2 = [x + width - rx, y];
        const control1 = [x + width - c * rx, y];
        const control2 = [x + width, y + c * ry];
        const p3 = [x + width, y + ry];
        const p4 = [x + width, y + height - ry];
        const control3 = [x + width, y + height - c * ry];
        const control4 = [x + width - c * rx, y + height];
        const p5 = [x + width - rx, y + height];
        const p6 = [x + rx, y + height];
        const control5 = [x + c * rx, y + height];
        const control6 = [x, y + height - c * ry];
        const p7 = [x, y + height - ry];
        const p8 = [x, y + ry];
        const control7 = [x, y + c * ry];
        const control8 = [x + c * rx, y];
        return [
            [p1, p2],
            [p2, control1, control2, p3],
            [p3, p4],
            [p4, control3, control4, p5],
            [p5, p6],
            [p6, control5, control6, p7],
            [p7, p8],
            [p8, control7, control8, p1],
        ];
    }
    static _getEdgeAssignmentForStyle(style, options, unmatchedStyles) {
        // If there are no edge assignment rules, treat every edge as an unspecified crease.
        if (!options.edgeAssignments) {
            return 'U';
        }
        const { mountains, valleys, creases, cuts, ignore, } = options.edgeAssignments;
        const assignmentRuleSets = [
            { assignment: null, ruleSet: ignore || [] },
            { assignment: 'M', ruleSet: mountains || [] },
            { assignment: 'V', ruleSet: valleys || [] },
            { assignment: 'U', ruleSet: creases || [] },
            { assignment: 'C', ruleSet: cuts || [] },
        ];
        // Find the first rule where one of the match conditions holds true.
        const matchedAssignmentRuleSet = assignmentRuleSets.find(assignmentRuleSet => SVGToCrease._doesStyleMatchRuleSet(style, assignmentRuleSet.ruleSet));
        if (matchedAssignmentRuleSet) {
            return matchedAssignmentRuleSet.assignment;
        }
        // Omit edges that don't match any rule.
        unmatchedStyles.add(JSON.stringify(style));
        return null;
    }
    static _doesStyleMatchRuleSet(style, ruleSet) {
        // The style matches the rule set if any one of its top level rules or rule lists matches.
        return ruleSet.some((ruleOrRules) => {
            if (Array.isArray(ruleOrRules)) {
                // There are several rules, all of which must match.
                return ruleOrRules.every(rule => SVGToCrease._doesStyleMatchRule(style, rule));
            }
            // There is only one rule, which must match.
            return SVGToCrease._doesStyleMatchRule(style, ruleOrRules);
        });
    }
    static _doesStyleMatchRule(style, rule) {
        switch (rule.type) {
            case 'color':
                return SVGToCrease._doesStyleMatchColorRule(style, rule);
            case 'dash':
                return SVGToCrease._doesStyleMatchDashRule(style, rule);
            case 'width':
                return SVGToCrease._doesStyleMatchWidthRule(style, rule);
            case 'group':
                return SVGToCrease._doesStyleMatchGroupRule(style, rule);
            default:
                throw new Error(`Unknown match type ${rule.type} for edge assignment.`);
        }
    }
    static _doesStyleMatchColorRule(style, rule) {
        // See if the Euclidean distance of the RGB color to the match value is within tolerance.
        const color = style.stroke;
        return color !== undefined && common_1.vec.distance(color, rule.value) < rule.tolerance;
    }
    static _doesStyleMatchDashRule(style, rule) {
        const dash = style['stroke-dasharray'];
        if (Array.isArray(rule.value)) {
            const valueArray = rule.value;
            if (!dash || dash.length !== valueArray.length) {
                return false;
            }
            // Check the dash array values.
            const tolerance = rule.tolerance || 0;
            return dash.every((element, i) => Math.abs(element - valueArray[i]) <= tolerance);
        }
        // When the rule's value is true or false, there's no need to check the dash array values.
        // We just check for the presence or absence of a dash array.
        if (rule.value === true) {
            return !!dash;
        }
        if (rule.value === false) {
            return !dash;
        }
        return false;
    }
    static _doesStyleMatchWidthRule(style, rule) {
        const width = style['stroke-width'];
        return (width !== undefined) && (width !== null)
            && Math.abs(width - rule.value) <= rule.tolerance;
    }
    static _doesStyleMatchGroupRule(style, rule) {
        // See if group matches any entry in an array of group IDs, or exactly one group ID.
        const { group } = style;
        if (Array.isArray(rule.value)) {
            return rule.value.some(id => id === group);
        }
        return group === rule.value;
    }
    static _saveSegments(fs, segments) {
        const formatNumber = (x) => (Math.round(x * 100) / 100).toString(10);
        const formatPoint = (p) => `${formatNumber(p[0])},${formatNumber(p[1])}`;
        const bounds = { min: [Infinity, Infinity], max: [-Infinity, -Infinity] };
        const paths = segments.map((segment, i) => {
            const b = common_1.geom.getBounds(segment.points);
            bounds.min = common_1.vec.min(bounds.min, b.min);
            bounds.max = common_1.vec.max(bounds.max, b.max);
            const points = segment.points.map(formatPoint);
            const command = ['L', 'Q', 'C'][points.length - 2];
            return `<path id="segment-${i}" class="edge ${segment.assignment}" d="M${points[0]}${command}${points.slice(1).join(' ')}"/>`;
        });
        const x = Number.isFinite(bounds.min[0]) ? bounds.min[0] : 0;
        const y = Number.isFinite(bounds.min[1]) ? bounds.min[1] : 0;
        const width = Number.isFinite(bounds.max[0]) ? bounds.max[0] - bounds.min[0] : 1;
        const height = Number.isFinite(bounds.max[1]) ? bounds.max[1] - bounds.min[1] : 1;
        const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="${x} ${y} ${width} ${height}" version="1.1">
    <style>
        .edge { stroke: black; stroke-width: 1; stroke-linecap: round; stroke-miterlimit: 10; fill: none; }
        .edge.M { stroke: #ff0000; }   /* mountain crease */
        .edge.V { stroke: #0000ff; }   /* valley crease */
        .edge.U { stroke: #ff00ff; }   /* unspecified crease */
        .edge.C { stroke: #00ff00; }   /* cut */
        .edge.F { stroke: #00ffff; }   /* facet crease */
    </style>
    ${paths.join('\n    ')}
</svg>`;
        fs.writeFileSync('SVGToCrease-debug.svg', svg);
    }
}
exports.SVGToCrease = SVGToCrease;
//# sourceMappingURL=SVGToCrease.js.map